import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import formatValue from 'scoreboard/components/format-value'
import i18n from 'scoreboard/components/i18n'
import * as thresholdsAndColors from 'scoreboard/components/thresholds-and-colors'
import {
	getThresholdColorsModel,
	validThresholds
} from 'scoreboard/components/thresholds-and-colors'
import config from 'scoreboard/components/config'
import nonAbbreviatedValuesTooltip from 'scoreboard/components/non-abbreviated-values-tooltip.hbs'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import ScorecardNode from 'scoreboard/scorecards/scorecard-node'
const { metricish } = ScorecardNode
import cleanObject from 'scoreboard/components/clean-object'
import constants from 'scoreboard/components/constants'
import formatInitiativeCalculations from 'scoreboard/initiatives/format-initiative-calculations'
const { PERCENTAGE_REACHING_GOAL, PERCENTAGE_YES } = constants
import DataType from 'scoreboard/datasets/model/data-type'
import { DATA_SOURCE_PROPERTY } from 'scoreboard/dashboards/widgets/shape/shape-widget-properties'
import {nameToHex, SLATE} from '../colors'
import ThresholdColorsModel from '../threshold-colors/threshold-colors-model'
import { DATASETS, INITIATIVES, SCORECARDS } from '../sections'
const { PERCENT, NUMBER, YES_NO, THRESHOLD_ID } = DataType

/**
 * THIS FILE CONTAINS COMMON FUNCTIONALITY SHARED BY THE LINEAR AND RADIAL
 * GAUGES
 */

/**
 * Returns the title String for the gauge, if that option is turned on.
 */
function title (opt) {
	if (!opt.label) {
		return null
	}

	// #34029 leading whitespace will prevent label from being displayed,
	// so just trim it
	return _.trim(opt.labelText ?? opt.model.label)
}

/**
 * Returns array of subtitle Strings for the gauge, which is the organization name
 * and/or the period label for the data displayed
 */
function subtitle (opt) {
	let subtitles = []

	if (opt.organization && /* #58814 */ !_.isEmpty(opt.model)) {
		subtitles.push(opt.model.organization)
	}

	if (opt.period) {
		const periodId = opt.periodId
		if (!_.isNil(periodId)) {
			subtitles.push(calendars.periodsById[periodId].label)
		}
	}

	return _.isEmpty(subtitles) ? null : subtitles
}

/**
 * Returns the value object, e.g. { value: 2, color: 'green', formatted: '2%' }
 */
function value (opt) {
	var v = formattedValue(opt)
	if (!opt.value && v && v.formatted) {
		// we need to return the "value" object so the gauge can be drawn,
		// but opt.value=false indicates that the value shouldn't be displayed
		// (ie in the top-left of the speedometer). We do this by leaving off
		// the "formatted" property.
		delete v.formatted
	}
	return v
}

function formattedValue (opt) {
	// #4300 don't return a color when gray
	if (!opt.currentScore
		&& _.isNil(opt.datasetQueryResponse)
		&& !opt.currentCalculations) {
		return null
	}

	if (opt.model.dataType === YES_NO) {
		return formattedYesNoValue(opt)
	}

	let scoringType = config.scoringType(opt.model.scoringTypeId)
	if (_.get(scoringType, 'standardScoringType') == constants.GOAL_ONLY) {
		return formattedGoalOnlyValue(opt)
	}
	if (_.get(scoringType, 'isCustomText')) {
		return formattedCustomText(opt)
	}

	// grab the score or value as appropriate
	var r = raw(opt)

	// #4300 don't return a color when the raw value is missing ...
	if (isMissingData(r, opt)) {
		return null
	}

	// start building the return value
	let v = { color: nameToHex(opt?.currentScore?.color) }
	if ([DATASETS, INITIATIVES].includes(opt.section)) {
		if (opt.thresholdColoring) {
			let tcm = new ThresholdColorsModel(opt.thresholdColoring)
			v.color = tcm.color(r).formatHex()
		} else {
			let tcm = getThresholdColorsModel(opt)
			v.color = tcm.color(r).formatHex()
		}
	}

	// don't display a needle for unscored metrics or metrics with invalid thresholds
	if (_.isNumber(r) &&
		_.get(scoringType, 'standardScoringType') != constants.UNSCORED &&
		thresholds(opt)) {
		v.raw = r
	}

	if (isDisplayingChartedValue(opt)) {
		if (opt.section === INITIATIVES) {
			let formatter = initValueFormatter(opt)
			v.formatted = formatter(r)
		} else {
			var formatted = formatValue({
				value: r,
				dataType: opt.dataType ?? opt.model.dataType,
				precision: opt.precision ?? opt.model.precision,
				currencyCode: opt.currencyCode ?? opt.model.currencyCode
			})
			// important: only set the formatted value if it's not null or an
			// empty String
			if (formatted) {
				v.formatted = formatted
			}
		}
	}

	return v
}

function isMissingData (raw, opt) {
	if (_.isNil(raw)) {
		switch (opt.section) {
			case SCORECARDS:
				// #6301 #23048 if the score is N/A, we don't treat the absence
				// of data as "missing"
				return opt.currentScore?.applicable
			case INITIATIVES:
				// #59016 if the calculation is null, that means there's no
				// data to use for it, so we consider that "missing"
				return true
			default: return false
		}
	} else {
		// If there's a raw value, then it definitely isn't missing
		return false
	}

}

/**
 * Returns a formatter appropriate for initiative values
 */
function initValueFormatter(opt) {
	let formatOptions = {
		currencyCode: opt.model?.currencyCode ?? opt.domainObj?.currencyCode
	}
	let formatter = formatInitiativeCalculations(
		opt[DATA_SOURCE_PROPERTY], formatOptions)
	return formatter
}

/**
 * Returns true if the charted value should be displayed on the gauge
 */
function isDisplayingChartedValue(opt) {
	return !_.isNil(opt.datasetQueryResponse)
		|| metricish(opt.model)
		|| opt.section === 'initiatives'
}

function formattedYesNoValue(opt) {
	// handle % yes and # of yeses
	if (_.isNumber(opt.currentScore.actual)) {
		let percent = _.includes([PERCENTAGE_YES, PERCENTAGE_REACHING_GOAL],
			opt.model.aggregationType)
		let dataType = percent ? PERCENT : NUMBER
		if (opt.model.rollupAggregationType === PERCENTAGE_YES) { // #31661
			dataType = opt.currentScore.dataType
		}
		return {
			color: nameToHex(opt.currentScore.color),
			// point the needle based on the score
			raw: opt.currentScore.score,
			// but display the actual to the user
			formatted: formatValue({
				value: opt.currentScore.actual,
				dataType
			})
		}
	}

	// handle typical yes/no values

	var answer = { color: nameToHex(opt.currentScore.color) }
	if (!_.isNil(opt.currentScore.actual)) {
		var key = opt.currentScore.actual ? 'common.yes' : 'common.no'
		// thresholds are [ 0, 2, 4 ]
		answer.raw = opt.currentScore.actual ? 3 : 1
		answer.formatted = i18n.get(key)
	}
	return answer
}

function formattedGoalOnlyValue(opt) {
	var answer = {
		color: nameToHex(opt.currentScore.color),
		formatted: formatValue({
			value: opt.currentScore.actual,
			dataType: opt.model.dataType,
			precision: opt.model.precision,
			currencyCode: opt.model.currencyCode
		}),
		raw: opt.currentScore.actual
	}
	if (validThresholds(opt.currentScore.thresholds) &&
		// issue 6471: only supply a raw value if there is an actual value
		!_.isUndefined(opt.currentScore.actual)) {
		// thresholds are [ 0, 2, 4 ] (see thresholds function for explanation)
		answer.raw = opt.currentScore.score == config.maximumScore ? 3 : 1
		answer.unformatted = opt.currentScore.actual
	}
	return answer
}

function formattedCustomText(opt) {
	let scoringType = config.scoringType(opt.model.scoringTypeId)
	if (opt.currentScore.dataType == THRESHOLD_ID) {
		let threshold = config.threshold(opt.currentScore.actual)
		let color
		if (scoringType.kpiValueType == constants.UNSCORED_TEXT) {
			color = SLATE
		} else {
			let thresholdIndex = scoringType.thresholds.indexOf(threshold)
			color = nameToHex(scoringType.colors[thresholdIndex])
		}
		let answer = cleanObject({
			color,
			raw: _.get(threshold, 'score'),
			formatted: _.get(threshold, 'label')
		})
		return answer
	} else {
		return formattedYesNoValue(opt)
	}
}

/**
 * Returns the thresholds array
 */
function thresholds (opt) {
	var thresholds = thresholdsAndColors.thresholds(_.extend({ forceWorstFirst: config.speedometersRedOnLeft }, opt))

	let scoringType = config.scoringType(opt.model.scoringTypeId)
	if (validThresholds(thresholds) &&
		_.get(scoringType, 'standardScoringType') == constants.GOAL_ONLY) {
		// goal only gauges should appear just like yes/no.  we have to
		// lie to the gauge to force it to do what we want, because
		// when actual == goal we don't want the needle on the line between
		// red and green, but rather firmly in the green zone
		thresholds = [ 0, 2, 4 ]

		if (!config.speedometersRedOnLeft && !opt.model.higherBetter) {
			thresholds = thresholds.reverse()
		}
	}

	return thresholds
}

/**
 * Returns the colors array, e.g. [ RED_MEDIUM, YELLOW_MEDIUM, GREEN_MEDIUM ]
 */
function colors (opt) {
	return thresholdsAndColors.colors(_.extend({ forceWorstFirst: config.speedometersRedOnLeft }, opt))
}

/**
 * Returns the value for actual.raw, which varies based on whether it's a
 * dataset gauge or a gauge for a metric.
 */
function raw (opt) {
	switch (opt.section) {
		case 'datasets': return datasetRaw(opt)
		case 'initiatives': return initiativeRaw(opt)
	}
	// speedometers initially only supported scorecards, so if the section isn't
	// defined we'll assume scorecards
	return scorecardRaw(opt)
}

function datasetRaw (opt) {
	return opt.datasetQueryResponse?.value
}

function scorecardRaw (opt) {
	let { model, currentScore } = opt
	if (!currentScore) {
		return null
	}

	return metricish(model) ? currentScore.actual : currentScore.score
}

function initiativeRaw (opt) {
	return opt.currentCalculations?.calculations[opt[DATA_SOURCE_PROPERTY]]
}

// Create a non-abbreviated tooltip for values that need them on hover
function createNonAbbreviatedTooltip (el, selector, extraClass, header, value) {
	return new TooltipAndDetails({
		el,
		selector,
		tooltipContent: (e) => nonAbbreviatedValuesTooltip({ header, value }),
		tooltipOptions: {
			extraClass: 'tooltip-and-details non-abbreviated-tooltip gauge-abbreviated-tooltip ' + extraClass,
			placement: 'above'
		}
	})
}

export { title, subtitle, value, thresholds, colors, raw, initValueFormatter,
	createNonAbbreviatedTooltip, formattedValue, formattedCustomText }
